import { useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { fmtDateTime, get, html, post, put } from "../lib.js";
import { Badge, Field, Loading, toast, useAction, useForm, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";

// Missed Call Text-Back ("שיחה שלא נענתה ← וואטסאפ") on the Channels page: the switch, the webhook
// the phone system calls, its secret, the timing and guardrail choices, a test call, and the latest
// calls with what happened to each. Owners and admins only (src/calls/missed-routes.ts).

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast(tr("Copied")));

const STATUS = {
  received: { label: "Waiting", kind: "neutral" },
  sending: { label: "Sending", kind: "neutral" },
  messaged: { label: "Texted", kind: "ok" },
  skipped: { label: "Skipped", kind: "muted" },
  failed: { label: "Not sent", kind: "warn" },
};

const TEMPLATE_STATUS = {
  approved: "Approved by Meta",
  pending: "Waiting for Meta's review",
  rejected: "Rejected by Meta",
  failed: "Couldn't be submitted yet",
};

function Copyable({ value, label }) {
  return html`<div class="copy-row">
    <pre class="code" dir="ltr">${value}</pre>
    <button type="button" class="btn small" onClick=${() => copy(value)}>${label ?? tr("Copy")}</button>
  </div>`;
}

function Settings({ t, s, onSaved }) {
  const { values, bind } = useForm({
    send_delay_seconds: s.send_delay_seconds,
    dedupe_window_minutes: s.dedupe_window_minutes,
    quiet_hours: s.quiet_hours,
    template: s.template,
    business_numbers: s.business_numbers,
    fallback: s.fallback,
  });
  const [busy, run] = useAction();
  const submit = async (e) => {
    e.preventDefault();
    const res = await run(
      () =>
        put(`${t.base}/missed-calls`, {
          send_delay_seconds: Number(values.send_delay_seconds),
          dedupe_window_minutes: Number(values.dedupe_window_minutes),
          quiet_hours: values.quiet_hours,
          template: values.template.trim(),
          business_numbers: values.business_numbers,
          fallback: values.fallback,
        }),
      tr("Settings saved"),
    );
    onSaved(res.settings);
  };
  return html`<form onSubmit=${submit}>
    <div class="row">
      <${Field} label=${tr("Wait before sending (seconds)")} hint=${tr("If someone calls them back within this time, nothing is sent.")}><input type="number" min="0" max="600" ...${bind("send_delay_seconds")} /></${Field}>
      <${Field} label=${tr("One message per caller every (minutes)")} hint=${tr("A customer who calls three times gets one message.")}><input type="number" min="0" max="1440" ...${bind("dedupe_window_minutes")} /></${Field}>
    </div>
    <div class="row">
      <${Field} label=${tr("During quiet hours")} hint=${tr("Quiet hours are set on the AI agent page. The customer just called, so sending at once is usually right.")}>
        <select ...${bind("quiet_hours")}><option value="send">${tr("Send anyway")}</option><option value="hold">${tr("Wait until quiet hours end")}</option></select>
      </${Field}>
      <${Field} label=${tr("When WhatsApp can't be used")}>
        <select ...${bind("fallback")}><option value="create_callback_task">${tr("Create a call-back task")}</option><option value="none">${tr("Do nothing")}</option></select>
      </${Field}>
    </div>
    <${Field} label=${tr("Your own phone numbers")} hint=${tr("Your lines and mobiles, separated by commas. Calls from them are never answered. Staff alert phones are skipped too.")}><textarea rows="2" dir="ltr" placeholder="03-555-0101, 052-123-4567" ...${bind("business_numbers")}></textarea></${Field}>
    <${Field} label=${tr("Your own template (optional)")} hint=${tr("Leave empty to use RELAYZA's template. If you approved your own in WhatsApp Manager, enter its exact name; it must have one variable, your business name.")}><input dir="ltr" placeholder=${s.standard_template.name} ...${bind("template")} /></${Field}>
    <div class="form-actions"><button class="btn primary" disabled=${busy}>${tr("Save")}</button></div>
  </form>`;
}

function TestCall({ t, onDone }) {
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [busy, run] = useAction();
  const send = async (e) => {
    e.preventDefault();
    const res = await run(() => post(`${t.base}/missed-calls/test`, { phone }));
    setResult(res.event);
    onDone();
  };
  return html`<form onSubmit=${send}>
    <p class="muted small">${tr("Simulates a missed call from the number you enter, with no wait, even while the switch is off. Use your own mobile to see what customers get. Test calls are left out of reports.")}</p>
    <div class="row">
      <${Field} label=${tr("Test number")}><input required type="tel" dir="ltr" placeholder="050-123-4567" value=${phone} onInput=${(e) => setPhone(e.target.value)} /></${Field}>
    </div>
    <div class="form-actions"><button class="btn" disabled=${busy}>${tr("Send test event")}</button></div>
    ${result && html`<p class="small" role="status">${result.status === "messaged" ? tr("Sent. Check WhatsApp on that phone.") : tr("Not sent: {reason}", { reason: tr(result.reason ?? "") })}${result.task_id ? ` ${tr("A call-back task was added in Tasks.")}` : ""}</p>`}
  </form>`;
}

function Events({ t, events }) {
  if (!events.length) return html`<p class="muted small">${tr("No missed calls yet.")}</p>`;
  return html`<div class="table-wrap"><table class="table">
    <thead><tr><th>${tr("When")}</th><th>${tr("Caller")}</th><th>${tr("Status")}</th><th>${tr("Details")}</th></tr></thead>
    <tbody>${events.map((e) => {
      const st = STATUS[e.status] ?? { label: e.status, kind: "neutral" };
      return html`<tr>
        <td class="small">${fmtDateTime(e.occurred_at, t.timezone)}${e.provider === "test" ? html` <${Badge} kind="muted">${tr("Test")}</${Badge}>` : ""}</td>
        <td><bdi dir="ltr">${e.caller_number ?? tr("Withheld")}</bdi>${e.contact_name && html`<div class="muted small" dir="auto">${e.contact_name}</div>`}</td>
        <td><${Badge} kind=${st.kind}>${tr(st.label)}</${Badge}></td>
        <td class="small">${e.skip_reason ? tr(e.skip_reason) : ""}
          ${e.conversation_id && html` <a href=${`/inbox/${e.conversation_id}`}>${tr("Open chat")}</a>`}
          ${e.task_id && html` <a href="/tasks">${tr("Call-back task")}</a>`}</td>
      </tr>`;
    })}</tbody>
  </table></div>`;
}

export function MissedCallsCard({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/missed-calls`), [t.id]);
  const [settings, setSettings] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [busy, run] = useAction();
  if (!data) return html`<section class="card"><${Loading} /></section>`;
  const s = settings ?? data.settings;

  const toggle = async (e) => {
    const enabled = e.target.checked;
    const res = await run(() => put(`${t.base}/missed-calls`, { enabled }), enabled ? tr("Missed calls will be texted back") : tr("Missed call text-back turned off"));
    setSettings(res.settings);
  };
  const rotate = async () => {
    if (!confirm(tr("Make a new secret? The old one stops working at once, so update your phone system, Zapier or Make right after."))) return;
    const res = await run(() => post(`${t.base}/missed-calls/secret`), tr("New secret made"));
    setSettings(res.settings);
    setShowSecret(true);
  };
  const tpl = s.standard_template;
  const payload = JSON.stringify(s.example_payload, null, 2);

  return html`<section class="card">
    <div class="section-head"><h2>${tr("Missed calls to WhatsApp")}</h2>
      <label class="toggle"><input type="checkbox" checked=${s.enabled} disabled=${busy} onChange=${toggle} /> ${tr("Text back missed calls")}</label></div>
    <p class="muted small">${tr("When your phone system reports a call nobody answered, we send the caller a WhatsApp message from your business number. The AI agent then answers, quotes and books as usual.")}</p>
    ${!s.whatsapp_connected && html`<div class="callout warn small">${tr("Connect WhatsApp first. Until then, each missed call becomes a call-back task.")}</div>`}
    <${PageGuide} id="channels.missed_calls" />

    <h3>${tr("Webhook for your phone system")}</h3>
    <p class="muted small">${tr("Zapier, Make and most phone systems:")}</p>
    <${Copyable} value=${s.webhook_url} label=${tr("Copy URL")} />
    <p class="muted small">${tr("Kavkom:")}</p>
    <${Copyable} value=${s.kavkom_url} label=${tr("Copy URL")} />
    <p class="muted small">${tr("Secret. Send it as the header Authorization: Bearer <secret>, or sign the body with it (X-Relayza-Signature). Keep it private.")}</p>
    <div class="copy-row">
      <pre class="code" dir="ltr">${showSecret ? s.secret : "•".repeat(24)}</pre>
      <button type="button" class="btn small" onClick=${() => setShowSecret(!showSecret)}>${showSecret ? tr("Hide") : tr("Show")}</button>
      <button type="button" class="btn small" onClick=${() => copy(s.secret)}>${tr("Copy secret")}</button>
      <button type="button" class="btn small ghost" disabled=${busy} onClick=${rotate}>${tr("Make a new secret")}</button>
    </div>
    <p class="muted small">${tr("What to send (JSON):")}</p>
    <${Copyable} value=${payload} label=${tr("Copy example")} />

    <h3>${tr("Message")}</h3>
    <p class="small" dir="auto">${tr("Inside the 24-hour window: \"Hi, we saw you tried to call and we couldn't pick up. How can we help?\" Outside it, WhatsApp needs an approved template.")}</p>
    ${!s.template && html`<p class="muted small">${tr("RELAYZA's template {name}: {status}", { name: tpl.name, status: tpl.status ? tr(TEMPLATE_STATUS[tpl.status] ?? tpl.status) : tr("submitted when you turn this on") })}${tpl.error ? ` (${tpl.error})` : ""}</p>`}

    <h3>${tr("Settings")}</h3>
    <${Settings} key=${s.secret_rotated_at ?? "s"} t=${t} s=${s} onSaved=${setSettings} />

    <h3>${tr("Send a test event")}</h3>
    <${TestCall} t=${t} onDone=${reload} />

    <h3>${tr("Latest calls")}</h3>
    <${Events} t=${t} events=${data.events} />
  </section>`;
}
